import React, { useEffect, useMemo, useState } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import MDBox from "components/MDBox";
import { buildRmblInvoicePayload } from "./rmblInvoice";
import MDTypography from "components/MDTypography";
import Footer from "../../../../../examples/Footer";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import MDButton from "../../../../../components/MDButton";
import { useNavigate, useLocation } from "react-router-dom";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { appAlert } from "components/AppDialog";

import { CrmRowActions, ListIconButton, ListLink, CrmPageHeader, CrmActionButton, CrmNotice, CrmTableCard } from "../crm/shared/CrmUi";
const normalizeApiList = (res) => {
    const raw = res?.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.data?.data)) return raw.data.data;
    return [];
};

const formatDateTime = (d, t) => {
    if (!d && !t) return "-";
    if (!d && t) return t;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return t || "-";
    const date = dt.toLocaleDateString("tr-TR");
    return `${date}${t ? ` ${t}` : ""}`;
};

const formatMoney = (v, currency = "") => {
    const n = Number(v || 0);
    return `${n.toFixed(2)}${currency ? ` ${currency}` : ""}`;
};

const cardSx = {
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    background: "#fff",
    boxShadow: "none",
};

const infoCardSx = {
    p: 2,
    minWidth: 180,
    border: "1px solid #eef2f7",
    borderRadius: "12px",
    background: "#f9fbfd",
};

const MblListAndInvoice = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [mbls, setMbls] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "", type: "error" });
    const [selectedMbl, setSelectedMbl] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, mbl: null });

    const fetchMbls = async () => {
        try {
            const res = await CrudService.getMBLs();
            setMbls(normalizeApiList(res));
        } catch (e) {
            console.error("getMBLs error:", e);
            setMbls([]);
        }
    };

    useEffect(() => {
        fetchMbls();
    }, []);

    useEffect(() => {
        const prefill = location.state;
        if (!prefill?.fromMbl) return;

        const run = async () => {
            try {
                if (prefill?.shipments?.length) {
                    const t = prefill.totals || {};
                    const consolidationData = {
                        rmblNumber: prefill.rmbl?.rmblNumber,
                        mbl: prefill.rmbl,
                        shipments: prefill.shipments.map((s) => s.attributes),
                        totals: {
                            totalLdm: Number(t.ldm || 0),
                            totalCbm: Number(t.cbm || 0),
                            totalVolume: Number(t.volume || 0),
                            totalChargeableWeight: Number(t.chargeableWeight || 0),
                            totalGrossWeight: Number(t.grossWeight || 0),
                            plannedRouteCountries: prefill.rmbl?.plannedRouteCountries || [],
                        },
                    };

                    await CrudService.createConsolidation({ data: consolidationData });
                }

                await fetchMbls();
                navigate(location.pathname, { replace: true, state: {} });
            } catch (err) {
                console.error("fromMbl flow error:", err?.response?.data || err);
                setNotification({
                    value: true,
                    text: "RMBL flow failed. Please try again.",
                    type: "error",
                });
            }
        };

        run();
    }, [location.state, navigate, location.pathname]);

    const clickDeleteMbl = async () => {
        try {
            const id = deleteDialog?.mbl?.id;
            if (!id) return;

            await CrudService.deleteMBL(id);
            await fetchMbls();

            if (selectedMbl?.id === id) {
                setSelectedMbl(null);
            }

            setDeleteDialog({ open: false, mbl: null });
            setNotification({
                value: true,
                text: "RMBL deleted successfully",
                type: "success",
            });
        } catch (e) {
            console.error("deleteMBL error:", e);
            setDeleteDialog({ open: false, mbl: null });
            setNotification({
                value: true,
                text: "RMBL delete failed",
                type: "error",
            });
        }
    };

    const handleEditMbl = (mblId) => {
        navigate(`/shipping-order/connect-mbl?id=${mblId}`);
    };

    const handleGoShipmentForm = (shipmentId) => {
        navigate(`/shipping-order/create-shipment/details?id=${shipmentId}`);
    };

    const handleGoInvoice = () => {
        if (!selectedMbl?.attributes) return;

        const payload = buildRmblInvoicePayload(selectedMbl.attributes);
        if (!payload) {
            appAlert("This RMBL has no linked shipments. Invoice cannot be created.");
            return;
        }

        navigate("/create-invoice", {
            state: {
                shipments: payload.formattedShipments,
                linehaulServiceProviderMap: payload.linehaulServiceProviderMap,
                fromMbl: true,
                rmbl: selectedMbl.attributes,
                sourceType: "road"
            },
        });
    };

    const mblTableData = useMemo(() => {
        return {
            columns: [
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>RMBL No</span>, accessor: "rmblNumber" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>COB</span>, accessor: "cob" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>ETA</span>, accessor: "eta" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Seal</span>, accessor: "sealNumber" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>CMR</span>, accessor: "cmrNumber" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Plate / Container</span>, accessor: "plateContainerNumber" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Linked Shipments</span>, accessor: "shipmentCount" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>, accessor: "actions" },
            ],
            rows: mbls.map((mbl) => {
                const attr = mbl.attributes || {};
                const shipments = attr.shipments || attr.shipmentDetails || [];
                const shipmentCount = Array.isArray(shipments) ? shipments.length : 0;
                const isSelected = selectedMbl?.id === mbl.id;

                const rowStyle = {
                    background: isSelected ? "#eef6ff" : "transparent",
                    borderRadius: 8,
                    padding: "6px 8px",
                    cursor: "pointer",
                    fontWeight: isSelected ? 700 : 500,
                };

                return {
                    rmblNumber: (
                        <div style={rowStyle}>
                            <ListLink onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditMbl(mbl.id);
                                }}>
                                {attr.rmblNumber || "-"}
                            </ListLink>
                        </div>
                    ),

                    cob: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {formatDateTime(attr.cob, attr.cobTime)}
                        </div>
                    ),
                    eta: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {formatDateTime(attr.eta, attr.etaTime)}
                        </div>
                    ),
                    sealNumber: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {attr.sealNumber || "-"}
                        </div>
                    ),
                    cmrNumber: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {attr.cmrNumber || "-"}
                        </div>
                    ),
                    plateContainerNumber: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {attr.plateContainerNumber || "-"}
                        </div>
                    ),
                    shipmentCount: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            <MDBox
                                px={1.5}
                                py={0.5}
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "999px",
                                    background: isSelected ? "#dbeafe" : "#eef6ff",
                                    color: "#1565c0",
                                    fontWeight: 700,
                                    minWidth: 36,
                                }}
                            >
                                {shipmentCount}
                            </MDBox>
                        </div>
                    ),
                    actions: (
                        <CrmRowActions onEdit={() => handleEditMbl(mbl.id)} editTitle="Edit RMBL" onDelete={() => setDeleteDialog({ open: true, mbl })} deleteTitle="Delete RMBL"><ListIconButton title="View Details" onClick={() => setSelectedMbl(mbl)}><VisibilityOutlinedIcon /></ListIconButton></CrmRowActions>
                    ),
                };
            }),
        };
    }, [mbls, selectedMbl]);

    const mblShipmentTableData = useMemo(() => {
        if (!selectedMbl?.attributes) return null;
        const attr = selectedMbl.attributes;
        const shipments = attr.shipments || attr.shipmentDetails || [];
        if (!Array.isArray(shipments) || shipments.length === 0) return null;

        const fmt = (v) => (v || v === 0 ? Number(v).toFixed(4) : "0.0000");

        return {
            columns: [
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Shipment No</span>, accessor: "shipmentNo" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Customer</span>, accessor: "customer" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Total LDM</span>, accessor: "totalLdm" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Total CBM</span>, accessor: "totalCbm" },
                { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Chargeable Weight</span>, accessor: "totalChargeableWeight" },
            ],
            rows: shipments.map((s, idx) => {
                const row = s?.attributes || s;
                // id/_id lives as a sibling of attributes (JSON:API shape {id, attributes}),
                // not inside attributes itself.
                const shipmentIdValue = s?.id || s?._id || row.id || row._id;
                return {
                    shipmentNo: (
                        <ListLink onClick={() => handleGoShipmentForm(shipmentIdValue)}>
                            {`RHFL${row.shipmentNo || idx + 1}`}
                        </ListLink>
                    ),
                    customer: row.customer || "-",
                    totalLdm: fmt(row.totals?.totalLdm),
                    totalCbm: fmt(row.totals?.totalCbm),
                    totalChargeableWeight: fmt(row.totals?.totalChargeableWeight),
                };
            }),
        };
    }, [selectedMbl]);

    const selectedAttr = selectedMbl?.attributes || null;
    const selectedShipments = selectedAttr?.shipments || selectedAttr?.shipmentDetails || [];
    const selectedShipmentCount = Array.isArray(selectedShipments) ? selectedShipments.length : 0;
    const hasShipmentsForInvoice = selectedShipmentCount > 0;

    const linehaulDetails = selectedAttr?.linehaulDetails || {};
    const financialSummary = selectedAttr?.financialSummary || {};
    const summaryCurrency = financialSummary?.totalCurrency || linehaulDetails?.currency || "EUR";

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox pt={3} pb={3} display="flex" flexDirection="column" gap={2.5}>
                {notification.value && (
<CrmNotice tone={notification.type === "success" ? "green" : "red"}>{notification.text}</CrmNotice>
)}

                <CrmPageHeader
 title="RMBL Management"
 subtitle="View, edit, review shipment links, and continue invoice operations from RMBL records."
 actions={
<>
<CrmActionButton variant="secondary" icon={<RefreshIcon />} onClick={fetchMbls}>Refresh</CrmActionButton>
<CrmActionButton variant="secondary" icon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Back</CrmActionButton>
</>
}
/>

                <CrmTableCard title="RMBL list" summary={`${mbls.length} records · click a row or the view icon to see its details`}>
<DataTable table={mblTableData} canSearch />
</CrmTableCard>

                {selectedAttr ? (
                    <MDBox p={3} sx={cardSx}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                            <MDBox>
                                <MDTypography variant="h5" fontWeight="bold">
                                    RMBL Details
                                </MDTypography>
                                <MDTypography variant="button" color="text">
                                    {selectedAttr.rmblNumber || "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox display="flex" gap={1} flexWrap="wrap">
                                <CrmActionButton variant="secondary" icon={<EditIcon />} onClick={() => handleEditMbl(selectedMbl.id)}>Edit RMBL</CrmActionButton>

                                <CrmActionButton icon={<DescriptionOutlinedIcon />} onClick={handleGoInvoice} disabled={!hasShipmentsForInvoice}>Invoice Details</CrmActionButton>
                            </MDBox>
                        </MDBox>

                        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">COB</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatDateTime(selectedAttr.cob, selectedAttr.cobTime)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">ETA</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatDateTime(selectedAttr.eta, selectedAttr.etaTime)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Seal Number</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedAttr.sealNumber || "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">CMR Number</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedAttr.cmrNumber || "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Plate / Container</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedAttr.plateContainerNumber || "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Linked Shipments</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedShipmentCount}
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Linehaul & Financial Summary
                        </MDTypography>

                        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Linehaul Provider</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {linehaulDetails?.provider?.name || "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Linehaul Cost</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(linehaulDetails?.cost, linehaulDetails?.currency || summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Shipment Costs Total</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(financialSummary?.shipmentCostTotal, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Total Cost</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(financialSummary?.totalCost, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Total Selling Rate</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(financialSummary?.totalSellingRate, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox
                                sx={{
                                    ...infoCardSx,
                                    background: Number(financialSummary?.totalProfit || 0) >= 0 ? "#edf9f0" : "#fff1f2",
                                    border: Number(financialSummary?.totalProfit || 0) >= 0 ? "1px solid #b7ebc6" : "1px solid #f5c2c7",
                                }}
                            >
                                <MDTypography variant="caption" color="text">Total Profit</MDTypography>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    mt={0.5}
                                    color={Number(financialSummary?.totalProfit || 0) >= 0 ? "success" : "error"}
                                >
                                    {formatMoney(financialSummary?.totalProfit, summaryCurrency)}
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        {mblShipmentTableData ? (
                            <MDBox>
                                <MDTypography variant="h6" fontWeight="bold" mb={1}>
                                    Linked Shipments
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block" mb={2}>
                                    All shipment records connected to the selected RMBL are shown below.
                                </MDTypography>
                                <DataTable table={mblShipmentTableData} canSearch />
                            </MDBox>
                        ) : (
                            <MDBox
                                p={3}
                                sx={{
                                    border: "1px dashed #d0d7de",
                                    borderRadius: "12px",
                                    background: "#fafbfc",
                                    textAlign: "center",
                                }}
                            >
                                <MDTypography variant="button" fontWeight="medium">
                                    No linked shipment found for this RMBL.
                                </MDTypography>
                            </MDBox>
                        )}
                    </MDBox>
                ) : (
                    <MDBox
                        p={4}
                        sx={{
                            ...cardSx,
                            textAlign: "center",
                            background: "#fcfcfd",
                        }}
                    >
                        <ZoomInIcon sx={{ fontSize: 42, color: "#90a4ae", mb: 1 }} />
                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                            Select an RMBL record
                        </MDTypography>
                        <MDTypography variant="button" color="text">
                            Click any RMBL row or use the view icon to inspect RMBL details and linked shipments.
                        </MDTypography>
                    </MDBox>
                )}
            </MDBox>

            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, mbl: null })} maxWidth="xs" fullWidth>
                <DialogTitle>Delete RMBL</DialogTitle>
                <DialogContent>
                    <MDTypography variant="button">
                        Are you sure you want to delete{" "}
                        <b>{deleteDialog?.mbl?.attributes?.rmblNumber || "-"}</b>?
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton variant="outlined" color="dark" onClick={() => setDeleteDialog({ open: false, mbl: null })}>
                        Cancel
                    </MDButton>
                    <MDButton variant="contained" color="error" onClick={clickDeleteMbl}>
                        Delete
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default MblListAndInvoice;